import { BASIC } from 'shared/model/useThemeCookie'
import styled from 'styled-components'

const Style = {
	LoadingContainer: styled.div`
		padding: 16px;
		text-align: center;
		${({theme}) => theme.font.extra_small};
		color: ${({theme}) => theme.color.grey4};
		border: 1px solid ${({theme}) => theme.color.grey3};
		border-radius: 4px;
		background-color: transparent !important;
	`,

	EditorContainer: styled.div`
		overflow: hidden;
		transition: border-color 0.2s ease;
		background-color: transparent !important;
	`,

	Toolbar: styled.div`
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		padding: 8px;
		border-bottom: 1px solid ${({theme}) => theme.color.grey2};
		background-color: transparent !important;

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			gap: 4px;
			padding: 6px;
		}
	`,

	ToolbarGroup: styled.div`
		display: flex;
		gap: 4px;
		align-items: center;

		&:not(:last-child)::after {
			content: '';
			width: 1px;
			height: 20px;
			background-color: transparent !important;
			margin-left: 8px;
		}
	`,

	ToolbarButton: styled.button`
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: 32px;
		height: 32px;
		padding: 4px 8px;
		border: 1px solid transparent !important;
		border-radius: 4px;
		background-color: ${({theme, $active}) =>
			$active ? theme.color.major : theme.color.white};
		color: ${({theme, $active}) =>
			$active ? theme.color.white : theme.color.black};
		${({theme}) => theme.font.extra_small_bold};
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			background-color: ${({theme, $active}) =>
				$active ? theme.color.major : theme.color.grey1};
			border-color: ${({theme}) => theme.color.grey4};
		}

		&:active {
			transform: translateY(1px);
		}

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			min-width: 28px;
			height: 28px;
			padding: 2px 6px;
			${({theme}) => theme.font.tiny};
		}
	`,

	FontSizeSelect: styled.select`
		height: 32px;
		padding: 4px 8px;
		border: 1px solid transparent !important;;
		border-radius: 4px;
		background-color: ${({theme}) => theme.color.white};
		color: ${({theme}) => theme.color.black};
		${({theme}) => theme.font.extra_small};
		cursor: pointer;
		transition: all 0.2s ease;
		min-width: 80px;

		&:hover {
			background-color: ${({theme}) => theme.color.grey1};
			border-color: ${({theme}) => theme.color.grey4};
		}

		&:focus {
			outline: none;
			border-color: ${({theme}) => theme.color.major};
		}

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			height: 28px;
			padding: 2px 6px;
			${({theme}) => theme.font.tiny};
			min-width: 70px;
		}
	`,

	EditorContent: styled.div`
		padding: 8px 16px;
		min-height: 60px;
		cursor: text;
		background-color: transparent !important;

		.ProseMirror {
			outline: none;
			${({theme}) => theme.font.extra_small};
			line-height: 1.6;
			min-height: 40px;
			width: 100%;
			cursor: text;
			background-color: transparent !important;

			&[data-placeholder]:before {
				content: attr(data-placeholder);
				color: ${({theme}) => theme.color.grey4};
				pointer-events: none;
				height: 0;
				float: left;
			}

			h1,
			h2,
			h3,
			h4,
			h5,
			h6 {
				margin: 16px 0 8px 0;
				font-weight: 700;
			}

			h1 {
				${({theme}) => theme.font.large_bold};
			}

			h2 {
				${({theme}) => theme.font.medium_bold};
			}

			h3 {
				${({theme}) => theme.font.small_bold};
			}

			p {
				margin: 8px 0;
				${({theme}) => theme.font.extra_small};
			}

			ul,
			ol {
				padding-left: 24px;
				margin: 8px 0;
				color: ${({ theme }) => theme.color?.type === BASIC ? theme.color?.basic?.font : theme.color?.gradation?.font};

				li {
					margin: 4px 0;
					${({theme}) => theme.font.extra_small};
				color: ${({ theme }) => theme.color?.type === BASIC ? theme.color?.basic?.font : theme.color?.gradation?.font};
				}
			}

			blockquote {
				border-left: 3px solid ${({theme}) => theme.color.major};
				padding-left: 16px;
				margin: 16px 0;
				color: ${({theme}) => theme.color.grey4};
				${({theme}) => theme.font.extra_small};
			}

			strong {
				font-weight: 700;
			}

			em {
				font-style: italic;
			}

			s {
				text-decoration: line-through;
			}

			code {
				background-color: ${({theme}) => theme.color.grey1};
				padding: 2px 4px;
				border-radius: 2px;
				${({theme}) => theme.font.extra_small};
				font-family: 'Courier New', monospace;
			}

			pre {
				background-color: ${({theme}) => theme.color.grey1};
				padding: 12px;
				border-radius: 4px;
				overflow-x: auto;
				margin: 12px 0;

				code {
					background: none;
					padding: 0;
				}
			}

			hr {
				border: none;
				border-top: 2px solid ${({theme}) => theme.color.grey2};
				margin: 24px 0;
			}
		}

		/* Read-only styles */
		${({theme, $editable = true}) =>
			!$editable &&
			`
			.ProseMirror {
				cursor: default;
				background-color: transparent;
			}
		`}
	`,
}

export default Style
